import { inject, Injectable, InjectionToken } from "@angular/core";
import { heicTo } from "heic-to";
import { from, Observable } from "rxjs";

export type HeicToFn = typeof heicTo;

export const HEIC_TO = new InjectionToken<HeicToFn>("heicTo", {
  providedIn: "root",
  factory: () => heicTo,
});

@Injectable({
  providedIn: "root",
})
export class ImageConverterService {
  private readonly heicTo = inject(HEIC_TO);

  public convertHeicToWebP(file: File): Observable<File> {
    return from(
      this.heicTo({ blob: file, type: "image/png" })
        .then((pngBlob: Blob) => this.convertBlobToWebP(pngBlob))
        .then((webpBlob: Blob) => {
          const outputName = file.name.replace(/\.(heic|heif)$/i, ".webp");
          return new File([webpBlob], outputName, { type: "image/webp" });
        }),
    );
  }

  private convertBlobToWebP(blob: Blob): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not get canvas 2D context"));
          return;
        }

        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (webpBlob) => {
            if (webpBlob) {
              resolve(webpBlob);
            } else {
              reject(new Error("Canvas toBlob returned null"));
            }
          },
          "image/webp",
          0.8,
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Failed to load image for WebP conversion"));
      };

      img.src = url;
    });
  }
}
